/**
 * Session Guard Hook
 * Monitors user activity and enforces product-specific HIPAA inactivity timeouts.
 * Per auth-routing-eng-plan.md and HIPAA § 164.312(a)(2)(iii)
 *
 * Features:
 *  - Debounced activity tracking (max 1 timer reset per second)
 *  - Tab visibility pause/resume (hidden time never counts as inactivity)
 *  - Multi-tab sync via BroadcastChannel (activity/warning/logout shared across tabs)
 *
 * @module @ayla/auth/session-guard
 */

import { useEffect, useRef, useCallback } from "react";
import {
  SessionGuardConfig,
  SESSION_TIMEOUTS,
  INACTIVITY_WARNING_THRESHOLD,
} from "../types";

/** Events treated as user activity when no override is provided. */
export const DEFAULT_ACTIVITY_EVENTS = [
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
] as const;

type BroadcastMessage =
  { type: "ACTIVITY"; ts: number } | { type: "WARNING" } | { type: "LOGOUT" };

/**
 * Hook to monitor user inactivity and trigger HIPAA-compliant session timeout.
 *
 * @example
 * // Engage (30 min, 5 min warning — default)
 * const { extendSession } = useSessionGuard({
 *   product: 'ENGAGE',
 *   onLogout: () => handleSecurityLogout(),
 *   onWarning: () => setShowWarningModal(true),
 * });
 *
 * @example
 * // VisitAssist (24 h, 15 min warning)
 * const { extendSession } = useSessionGuard({
 *   product: 'VISIT_ASSIST',
 *   warningMs: 15 * 60 * 1000,
 *   onLogout: () => handleSecurityLogout(),
 *   onWarning: () => setShowWarningModal(true),
 * });
 */
export function useSessionGuard(config: SessionGuardConfig) {
  const {
    product,
    onLogout,
    onWarning,
    onActivity,
    warningMs: warningMsOverride,
    activities,
  } = config;

  // Timeout is product-controlled — not caller-overridable (HIPAA requirement).
  const timeoutMs = SESSION_TIMEOUTS[product];

  // "Lead time" = how many ms before the logout deadline to show the warning.
  // Default: 5 minutes (INACTIVITY_WARNING_THRESHOLD).
  const warningLeadMs = warningMsOverride ?? INACTIVITY_WARNING_THRESHOLD;

  // -------------------------------------------------------------------------
  // Refs — avoid re-registering listeners on every render
  // -------------------------------------------------------------------------

  const lastActivityRef = useRef<number>(Date.now());
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasWarnedRef = useRef(false);
  /** Timestamp of the most recent timer reset — used for 1-per-second debounce. */
  const lastResetTsRef = useRef<number>(0);
  /** When the tab was hidden; null when visible. */
  const hiddenAtRef = useRef<number | null>(null);
  const broadcastRef = useRef<BroadcastChannel | null>(null);

  // Keep latest callbacks in refs so effects never need to re-register due to
  // callback identity changes (common pattern for stable event handlers).
  const onLogoutRef = useRef(onLogout);
  const onWarningRef = useRef(onWarning);
  const onActivityRef = useRef(onActivity);
  useEffect(() => {
    onLogoutRef.current = onLogout;
    onWarningRef.current = onWarning;
    onActivityRef.current = onActivity;
  });

  // Stable ref for the activities list — callers should pass a module-level
  // constant; this ref guards against accidental re-registration.
  const eventsRef = useRef<readonly string[]>(
    activities ?? DEFAULT_ACTIVITY_EVENTS
  );

  // -------------------------------------------------------------------------
  // Core timer management
  // -------------------------------------------------------------------------

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current !== null) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (timeoutTimerRef.current !== null) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
  }, []);

  /**
   * Schedule warning and logout timers relative to *now*, using `remainingMs`
   * as the deadline. Called both on full resets and on visibility-resume with
   * a reduced remaining window.
   */
  const scheduleFromNow = useCallback(
    (remainingMs: number) => {
      clearTimers();

      // Warning fires `warningLeadMs` before the logout deadline.
      const warningDelay = remainingMs - warningLeadMs;

      if (onWarningRef.current && warningDelay > 0) {
        warningTimerRef.current = setTimeout(() => {
          if (hasWarnedRef.current) return; // guard against duplicate fires
          hasWarnedRef.current = true;
          onWarningRef.current?.();
          try {
            broadcastRef.current?.postMessage({
              type: "WARNING",
            } satisfies BroadcastMessage);
          } catch {
            // BroadcastChannel may already be closed
          }
        }, warningDelay);
      }

      timeoutTimerRef.current = setTimeout(() => {
        onLogoutRef.current();
        try {
          broadcastRef.current?.postMessage({
            type: "LOGOUT",
          } satisfies BroadcastMessage);
        } catch {
          // BroadcastChannel may already be closed
        }
      }, remainingMs);
    },
    [clearTimers, warningLeadMs]
  );

  /** Full reset: record new last-activity time and restart both timers. */
  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    hasWarnedRef.current = false;
    scheduleFromNow(timeoutMs);
  }, [scheduleFromNow, timeoutMs]);

  // -------------------------------------------------------------------------
  // Activity handler (debounced — max 1 timer reset per second)
  // -------------------------------------------------------------------------

  const handleActivity = useCallback(() => {
    // Once the warning has fired, local activity should not silently extend
    // the session. The user must explicitly click "Stay Logged In".
    if (hasWarnedRef.current) return;

    const now = Date.now();
    if (now - lastResetTsRef.current < 1000) return; // debounce
    lastResetTsRef.current = now;

    resetTimers();
    onActivityRef.current?.();
    try {
      broadcastRef.current?.postMessage({
        type: "ACTIVITY",
        ts: now,
      } satisfies BroadcastMessage);
    } catch {
      // BroadcastChannel may already be closed
    }
  }, [resetTimers]);

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  /**
   * Call when the user clicks "Stay Logged In" in the warning modal.
   * Clears the warned state and restarts timers from zero.
   */
  const extendSession = useCallback(() => {
    hasWarnedRef.current = false;
    lastResetTsRef.current = 0; // clear debounce so next activity is processed
    resetTimers();
    onActivityRef.current?.();
    try {
      broadcastRef.current?.postMessage({
        type: "ACTIVITY",
        ts: Date.now(),
      } satisfies BroadcastMessage);
    } catch {
      // BroadcastChannel may already be closed
    }
  }, [resetTimers]);

  // -------------------------------------------------------------------------
  // BroadcastChannel — multi-tab sync
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;

    const channel = new BroadcastChannel(`session-guard-${product}`);
    broadcastRef.current = channel;

    channel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
      const { type } = event.data;

      if (type === "ACTIVITY") {
        // Sync timer reset — don't re-broadcast to avoid infinite loop.
        lastActivityRef.current = Date.now();
        hasWarnedRef.current = false;
        onActivityRef.current?.();
        scheduleFromNow(timeoutMs);
      } else if (type === "WARNING") {
        if (!hasWarnedRef.current) {
          hasWarnedRef.current = true;
          onWarningRef.current?.();
        }
      } else if (type === "LOGOUT") {
        onLogoutRef.current();
      }
    };

    return () => {
      channel.close();
      broadcastRef.current = null;
    };
  }, [product, scheduleFromNow, timeoutMs]);

  // -------------------------------------------------------------------------
  // Activity event listeners + initial timer
  // -------------------------------------------------------------------------

  useEffect(() => {
    const events = eventsRef.current;

    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    resetTimers();

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearTimers();
    };
  }, [handleActivity, resetTimers, clearTimers]);

  // -------------------------------------------------------------------------
  // Tab visibility — pause timer when hidden, resume with correct remaining time
  // -------------------------------------------------------------------------

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
        clearTimers(); // don't count hidden time as inactivity
      } else if (
        document.visibilityState === "visible" &&
        hiddenAtRef.current !== null
      ) {
        const now = Date.now();
        const hiddenDuration = now - hiddenAtRef.current;
        hiddenAtRef.current = null;

        // Shift the last-activity anchor forward so hidden time is excluded.
        lastActivityRef.current += hiddenDuration;

        const elapsed = now - lastActivityRef.current;
        const remaining = timeoutMs - elapsed;

        if (remaining <= 0) {
          // Timeout would have fired while hidden — log out immediately.
          onLogoutRef.current();
          return;
        }

        // If we re-enter the warning window on becoming visible, fire immediately.
        const inWarningWindow = elapsed >= timeoutMs - warningLeadMs;
        if (inWarningWindow && !hasWarnedRef.current) {
          hasWarnedRef.current = true;
          onWarningRef.current?.();
        }

        scheduleFromNow(remaining);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [clearTimers, scheduleFromNow, timeoutMs, warningLeadMs]);

  return {
    /** Call when the user clicks "Stay Logged In" to extend their session. */
    extendSession,
    /** Returns ms remaining until logout (based on last activity time). */
    getTimeRemaining: () => {
      const elapsed = Date.now() - lastActivityRef.current;
      return Math.max(0, timeoutMs - elapsed);
    },
  };
}
